import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {I18nDictionaryComponent} from '../../../shared/components/i18n-dictionary/i18n-dictionary.component';
import {QuoteConstants} from '../../../quotes/model/quote';
import {Registration} from '../../models/user';
import {AuthError} from '../../models/auth-error';
import {Location} from '@angular/common';
import {LanguageService} from '../../../core/services/language.service';
import {Country} from 'src/app/quotes/model/country';
import { StoreService } from 'src/app/core/services/store.service';

@Component({
    selector: 'app-registration-form',
    templateUrl: './registration-form.component.html',
    styleUrls: ['./registration-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationFormComponent {
    readonly fieldFirstNameTextId = 'field-first-name';
    readonly fieldLastNameTextId = 'field-last-name';


    readonly fieldEmailTextId = 'email-placeholder';
    readonly fieldPasswordTextId = 'password-placeholder';
    readonly fieldConfirmPasswordTextId = 'confirm-password-placeholder';
    readonly fieldCompanyTextId = 'company-placeholder';
    readonly fieldStreetTextId = 'street-placeholder';
    readonly fieldCountryTextId = 'country-placeholder';
    readonly fieldCityTextId = 'city-placeholder';
    readonly fieldZipTextId = 'zip-placeholder';
    readonly fieldVatTextId = 'vat-placeholder';
    readonly fieldPhoneTextId = 'phone-placeholder';

    get currentLang(): string {
        return this.languageService.getCurrentLanguage().href;
    }

    @Input()
    set pending(isPending: boolean) {
        this.isPending = isPending;
        if (isPending) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    _countries: Country[];

    @Input()
    set countries(countries: Country[]) {
        this._countries = countries
            .filter(c => c.name != null)
            .sort((c1, c2) => c1.name > c2.name ? 1 : -1);

        this.countryIdControl.reset('');
    }

    isPending = false;

    @Input()
    registrationSuccessful: boolean;

    @Input()
    error: AuthError;

    @ViewChild(I18nDictionaryComponent, {static: true})
    i18nDictionary: I18nDictionaryComponent;

    @Input()
    countriesIsPending: boolean;

    @Output()
    registration = new EventEmitter<Registration>();

    readonly firstNameMaxLength = QuoteConstants.ADDRESS_FIRST_NAME_MAX_LENGTH;
    readonly lastNameMaxLength = QuoteConstants.ADDRESS_LAST_NAME_MAX_LENGTH;

    form = new FormGroup({
        firstName: new FormControl('', [
            Validators.required,
            Validators.maxLength(this.firstNameMaxLength)
        ]),
        lastName: new FormControl('', [
            Validators.required,
            Validators.maxLength(this.lastNameMaxLength)
        ]),
        password: new FormControl('', [
            Validators.pattern(
                // Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&#+~)(=/"^°<>§]{8,}$/
            ),
            Validators.required
        ]),
        confirmPassword: new FormControl('', [
            Validators.required
        ]),
        email: new FormControl('', [
            Validators.email,
            Validators.required
        ]),
        captcha: new FormControl('', [
            Validators.required
        ]),
        company: new FormControl('', [
            Validators.required
        ]),
        street: new FormControl('', [
            Validators.required
        ]),
        countryId: new FormControl('', [
            Validators.required
        ]),
        zipCode: new FormControl('', [
            Validators.required
        ]),
        city: new FormControl('', [
            Validators.required
        ]),
        phone: new FormControl('', [
            Validators.required
        ]),
        vat: new FormControl('')
    }, {
        validators: [this.match('password', 'confirmPassword')]
    });

    constructor(private _location: Location, private languageService: LanguageService, private storeService: StoreService) {
    }

    match(controlName: string, checkControlName: string): ValidatorFn {
        return (controls: AbstractControl) => {
            const control = controls.get(controlName);
            const checkControl = controls.get(checkControlName);

            if (checkControl.errors && !checkControl.errors.mismatch) {
                return null;
            }

            if (control.value !== checkControl.value) {
                controls.get(checkControlName).setErrors({mismatch: true});
                return {mismatch: true};
            } else {
                return null;
            }
        };
    }

    get firstNameControl(): AbstractControl {
        return this.form.get('firstName');
    }

    get lastNameControl(): AbstractControl {
        return this.form.get('lastName');
    }

    get passwordControl(): AbstractControl {
        return this.form.get('password');
    }

    get confirmPasswordControl(): AbstractControl {
        return this.form.get('confirmPassword');
    }

    get emailControl(): AbstractControl {
        return this.form.get('email');
    }

    get captchaControl(): AbstractControl {
        return this.form.get('captcha');
    }

    get streetControl(): AbstractControl {
        return this.form.get('street');
    }

    get companyControl(): AbstractControl {
        return this.form.get('company');
    }

    get cityControl(): AbstractControl {
        return this.form.get('city');
    }

    get zipControl(): AbstractControl {
        return this.form.get('zipCode');
    }

    get countryIdControl(): AbstractControl {
        return this.form.get('countryId');
    }

    get vatControl(): AbstractControl {
        return this.form.get('vat');
    }

    get phoneControl(): AbstractControl {
        return this.form.get('phone');
    }

    register() {
        this.form.markAllAsTouched();

        if (!this.form.valid) {
            return;
        }

        const value = this.form.value;
        this.registration.emit({
            customer: {
                addresses: [{
                    firstname: value.firstName,
                    lastname: value.lastName,
                    company: value.company,
                    city: value.city,
                    street: [value.street],
                    country_id: value.countryId,
                    postcode: value.zipCode,
                    telephone: value.phone,
                    default_billing: true,
                    default_shipping: true
                }],
                email: value.email,
                firstname: value.firstName,
                lastname: value.lastName,
                taxvat: value.vat,
                store_id: this.storeService.getStoreId(),
                website_id: null
            },
            password: value.password,
            captcha: value.captcha
        });
    }

    goBack() {
        // Return to last page visited
        this._location.back();
    }

}
